import { useApp } from "../state/store";

/**
 * Landing on a new shell, in one reload, without ever taking a recording with it.
 *
 * Whether a worker was already in control when this page loaded, read at module evaluation
 * -- which is before boot registers anything. On a first-ever visit there is no controller,
 * the worker installs and claims, and `controllerchange` fires for that first claim;
 * treating it as an update would reload somebody's very first load for nothing.
 */
const hadController = Boolean(navigator.serviceWorker?.controller);

let updateReady = false;
let reloadingForUpdate = false;
let lastUpdateCheck = 0;

/**
 * How often to ask the browser to re-fetch `sw.js`. Switching between apps fires
 * `visibilitychange` constantly and each check is a real request, so this is throttled for
 * the same reason the session state poll is.
 */
const UPDATE_CHECK_MS = 60000;

/** Long enough to read, short enough not to feel like a stall. */
const TOAST_MS = 1200;

/**
 * Whether the reload may happen now, as a pure function of the three things that decide it.
 *
 * Separated from the effect so the guard can be asserted without a browser -- the effect is
 * a `location.reload()`, which is precisely what a test cannot let run.
 */
export function shouldApplyUpdate(state: {
  updateReady: boolean;
  reloadingForUpdate: boolean;
  recording: boolean;
  stopping: boolean;
  /** Start or Join is opening the microphone for a session that already exists (#658). */
  settingUp?: boolean;
}): boolean {
  if (!state.updateReady || state.reloadingForUpdate) return false;
  // A reload between Start and the first audio would leave the session it just created with
  // nobody in it, so setup waits like the meeting itself does.
  if (state.settingUp) return false;
  /*
   * The one that is not bookkeeping, and it is two flags rather than one.
   *
   * [V] It was `!state.recording` alone, and that is not the window. `recording` clears
   * when the microphone is released, which happens at the *start* of wrap-up with the final
   * scoring pass, the claim drain, the tail audio upload and the finalise still to come --
   * ten to thirty seconds of the most irreplaceable work the app does. Worse, the release
   * itself is the trigger: `installUpdateFlow` applies a deferred update the moment
   * recording goes true -> false, so an update that arrived mid-session reloaded the page
   * 1.2 s into wrap-up, every time. The outbox and the tail audio go with the reload and
   * the session is never finalised for the table.
   *
   * `beforeunload` never had this bug: it guards on the recorder reference, which lives
   * until teardown. `stopping` is that same window, and the pair is the honest condition.
   */
  return !state.recording && !state.stopping;
}

/**
 * A new shell has taken over: reload onto it, unless this device is recording.
 *
 * **The guard defers the reload, not the update.** `sw.js` calls `skipWaiting()` at install,
 * so a new worker activates whenever the browser happens to re-check `sw.js`, and from that
 * moment its fetch handler serves from the *new* cache while this page runs the code it
 * already parsed. That mixture is not made better or worse here.
 *
 * What must not happen mid-session is the *reload*. It would take the recorder, the audio
 * buffer and the un-posted claim outbox with it -- the same loss `beforeunload` guards, and
 * for the same reason. [M] Losing the outbox is not hypothetical: one phone on a slow uplink
 * held four bundles at once for over a minute.
 *
 * The condition is the store's `recording` flag, not the presence of a session: the engine
 * sets it while a microphone is open and clears it the moment the recorder is released,
 * which is precisely the window in which a reload destroys something.
 */
export function applyShellUpdate(): void {
  const { recording, stopping, settingUp } = useApp.getState();
  if (!shouldApplyUpdate({ updateReady, reloadingForUpdate, recording, stopping, settingUp })) {
    return;
  }
  reloadingForUpdate = true;
  const toast = document.getElementById("update-toast");
  if (!toast) {
    location.reload();
    return;
  }
  toast.hidden = false;
  // On its own timer rather than chained off the transition, so reduced motion -- where
  // there is no transition to end -- cannot leave somebody parked on a toast that never
  // resolves.
  setTimeout(() => location.reload(), TOAST_MS);
  requestAnimationFrame(() => toast.classList.add("in"));
}

/**
 * Ask the browser to look for a new `sw.js`, on returning to the app.
 *
 * Without this the check only happens on navigation, which is what made a deploy take two
 * loads to land: the first served the old shell while the new worker installed behind it.
 * Somebody who leaves the app and comes back has given us a moment where reloading costs
 * them nothing.
 */
export async function checkForShellUpdate(): Promise<void> {
  if (document.visibilityState !== "visible") return;
  // An update that arrived while the app was in the background, or during a session that has
  // since ended, is applied here rather than waiting for another one.
  applyShellUpdate();
  const { recording, stopping } = useApp.getState();
  if (recording || stopping || reloadingForUpdate) return;
  const now = Date.now();
  if (now - lastUpdateCheck < UPDATE_CHECK_MS) return;
  lastUpdateCheck = now;
  const reg = await navigator.serviceWorker?.getRegistration?.();
  await reg?.update?.();
}

/**
 * Register the worker and wire the update flow. Called from boot **last**, after the first
 * screen is up: nothing above it should wait on a service worker, and a worker that claims
 * mid-boot would be racing the model download for no benefit.
 */
export function installUpdateFlow(): void {
  if (!("serviceWorker" in navigator)) return;

  /*
   * Development never registers the worker, and removes any earlier dev session's.
   *
   * `sw.js` answers every same-origin GET with stale-while-revalidate -- the right
   * strategy for content-hashed build output, and exactly wrong for Vite's unhashed
   * `/src/*` modules: the first dev load caches them, every reload after serves that
   * snapshot while refreshing in the background, and the page runs a generation behind
   * the files on disk. HMR cannot rescue it -- its busted requests match the stale entry
   * through `ignoreSearch` too. `import.meta.env.DEV` is false in `vite build`, so
   * production is untouched; the unregister is for a browser that already registered the
   * worker in dev, which nothing but removal heals.
   */
  if (import.meta.env.DEV) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((reg) => void reg.unregister()))
      .catch(() => {});
    return;
  }

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController) return;
    updateReady = true;
    applyShellUpdate();
  });

  // `visibilitychange` rather than `focus`, because it is the one that fires when a phone's
  // screen goes off.
  document.addEventListener("visibilitychange", () => {
    void checkForShellUpdate();
  });

  /*
   * The deferred reload needs something to release it, and `app.js` had nothing.
   *
   * There, an update that arrived mid-recording waited for the next `visibilitychange` --
   * so somebody who stopped recording and stayed in the app sat on the old shell until they
   * switched away and back. Subscribing to the flag the guard already reads closes that:
   * the moment the recorder is released, the reload it was holding off applies. Added
   * deliberately rather than ported; the guard's *condition* is unchanged.
   */
  const busy = (state: { recording: boolean; stopping: boolean; settingUp: boolean }) =>
    state.recording || state.stopping || state.settingUp;
  let wasBusy = busy(useApp.getState());
  useApp.subscribe((state) => {
    const now = busy(state);
    if (wasBusy && !now) applyShellUpdate();
    wasBusy = now;
  });

  navigator.serviceWorker.register("/sw.js").catch(() => {
    /* No worker means no offline and no updates, which is a degraded app rather than a
       broken one -- and nothing above this line depends on it. */
  });
}
